import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Divider,
  Toolbar,
  Typography,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import LogoutIcon from "@mui/icons-material/Logout";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import { getDefaultSidebarData } from '../Utils/sidebarData';

const drawerWidth = 260;
const SIDEBAR_ORDER_KEY = 'sidebarItemOrder';

function Sidebar({ open, onClose }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [sidebarData, setSidebarData] = useState(getDefaultSidebarData());
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Colors based on User Request
  const primaryColor = "#FF6600";
  const sidebarBg = "#111111"; // True Dark
  const textPrimary = "#FFFFFF";
  const textSecondary = "#888888";

  // Load sidebar order from localStorage on mount
  useEffect(() => {
    const savedOrder = localStorage.getItem(SIDEBAR_ORDER_KEY);
    const defaultData = getDefaultSidebarData();
    if (savedOrder) {
      try {
        const order = JSON.parse(savedOrder);
        const orderedData = order
          .map(id => defaultData.find(item => item.id === id))
          .filter(Boolean);

        const existingIds = new Set(order);
        const newItems = defaultData.filter(item => !existingIds.has(item.id));

        setSidebarData([...orderedData, ...newItems]);
      } catch (error) {
        console.error('Error loading sidebar order:', error);
      }
    }
  }, []);

  const saveSidebarOrder = (newOrder) => {
    const order = newOrder.map(item => item.id);
    localStorage.setItem(SIDEBAR_ORDER_KEY, JSON.stringify(order));
  };

  const toggleMenu = (index) => {
    setOpenMenu(openMenu === index ? null : index);
  };

  const handleDragStart = (e, index) => {
    setIsDragging(true);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newSidebarData = [...sidebarData];
    const draggedItem = newSidebarData[draggedIndex];
    newSidebarData.splice(draggedIndex, 1);
    newSidebarData.splice(dropIndex, 0, draggedItem);

    setSidebarData(newSidebarData);
    saveSidebarOrder(newSidebarData);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Drawer
      variant={isSmallScreen ? "temporary" : "permanent"}
      open={isSmallScreen ? open : true}
      onClose={isSmallScreen ? onClose : undefined}
      sx={{
        width: isSmallScreen ? drawerWidth : (open ? drawerWidth : 88),
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width: isSmallScreen ? drawerWidth : (open ? drawerWidth : 88),
          boxSizing: 'border-box',
          backgroundColor: sidebarBg,
          color: textSecondary,
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid rgba(255,255,255,0.05)',
        },
      }}
    >
      <Toolbar sx={{ px: [2], mt: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: open ? 'space-between' : 'center' }}>
        {open && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                backgroundColor: primaryColor,
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 0 15px ${primaryColor}44`
              }}
            >
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>V</Typography>
            </Box>
            <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700, letterSpacing: -0.5, fontSize: '1.2rem' }}>
              Varahi
            </Typography>
          </Box>
        )}
        <IconButton onClick={onClose} sx={{ color: textSecondary, "&:hover": { color: primaryColor } }}>
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </Toolbar>

      <List sx={{ px: 2, mt: 2, flexGrow: 1 }}>
        {sidebarData.map((item, index) => (
          <Box
            key={item.id || index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            sx={{
              position: 'relative',
              mb: 1,
              borderTop: dragOverIndex === index && draggedIndex !== null ? `2px solid ${primaryColor}` : 'none',
            }}
          >
            <ListItemButton
              component={item.children ? "div" : NavLink}
              to={item.children ? undefined : item.path}
              end
              onClick={() => item.children && toggleMenu(index)}
              sx={{
                minHeight: 50,
                justifyContent: open ? 'initial' : 'center',
                px: 2,
                borderRadius: '10px',
                transition: 'all 0.2s ease',
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  color: textPrimary,
                  "& .MuiListItemIcon-root": { color: primaryColor },
                },
                "&.active": {
                  backgroundColor: primaryColor,
                  color: textPrimary,
                  boxShadow: `0 8px 20px ${primaryColor}33`,
                  "& .MuiListItemIcon-root": { color: textPrimary },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 2 : 0,
                  justifyContent: 'center',
                  color: 'inherit',
                  transition: 'color 0.2s',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                  sx={{ color: 'inherit' }}
                />
              )}
              {open && item.children && (
                <Box sx={{ color: 'inherit', ml: 1 }}>
                  {openMenu === index ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </Box>
              )}
            </ListItemButton>

            {item.children && (
              <Collapse in={openMenu === index} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ mt: 0.5 }}>
                  {item.children.map((child, cIndex) => (
                    <ListItemButton
                      key={child.id || `${item.id}-child-${cIndex}`}
                      component={NavLink}
                      to={child.path}
                      end
                      sx={{
                        pl: open ? 5.5 : 2,
                        minHeight: 38,
                        borderRadius: '8px',
                        mx: 0.5,
                        mb: 0.5,
                        color: textSecondary,
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.03)",
                          color: textPrimary
                        },
                        "&.active": {
                          color: primaryColor,
                          "&:before": {
                            content: '""',
                            position: 'absolute',
                            left: 20,
                            width: 5,
                            height: 5,
                            borderRadius: '50%',
                            backgroundColor: primaryColor,
                          }
                        }
                      }}
                    >
                      <ListItemText
                        primary={child.title}
                        primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>

      <Box sx={{ p: 2, pb: 4 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            minHeight: 50,
            justifyContent: open ? 'initial' : 'center',
            px: 2,
            borderRadius: '10px',
            color: "#FF4D4D",
            "&:hover": {
              backgroundColor: "rgba(255, 77, 77, 0.1)",
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: open ? 2 : 0, color: 'inherit' }}>
            <LogoutIcon />
          </ListItemIcon>
          {open && (
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
            />
          )}
        </ListItemButton>
      </Box>
    </Drawer>
  );
}

export default Sidebar;
